/**
 * PendingQueueScreen.tsx
 * Écran de gestion de la file d'attente de synchronisation.
 *
 * @version 1.2.0 – brouillons cliquables → reprise EDL (handleResumeDraft)
 */

import { useEffect, useState, useCallback, memo } from 'react'
import {
  RefreshCw, Trash2, ChevronLeft, ChevronRight, WifiOff, Wifi,
  Clock, Camera, Upload, AlertTriangle, CheckCircle2, Inbox,
} from 'lucide-react'
import { useAppStore }                        from '../store/useAppStore'
import { edlDraftStore }                      from '../store/useEdlDraftStore'
import { offlinePhotoStore, PhotoGroup }      from '../store/offlinePhotoStore'
import Button                                 from '../components/ui/Button'
import Card                                   from '../components/ui/Card'
import toast                                  from 'react-hot-toast'
import { useSyncStatus }                      from '../store/useSyncStatus'

// ── Types ──────────────────────────────────────────────────────────────────
interface DraftEntry {
  contractId: string
  type:       'depart' | 'retour'
  savedAt:    string
  plate?:     string
}

// ── Constantes ─────────────────────────────────────────────────────────────
const POLL_MS = 15_000

// ── Helpers ────────────────────────────────────────────────────────────────
function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '–' }
}

function typeLabel(t: string): string {
  return t === 'depart' ? 'Départ' : 'Retour'
}

// ── Sous-composant : badge réseau ──────────────────────────────────────────
const NetworkBadge = memo(function NetworkBadge({ online }: { online: boolean }) {
  return online ? (
    <div className="flex items-center gap-1.5 text-green-600 text-xs font-semibold">
      <Wifi size={13}/> En ligne
    </div>
  ) : (
    <div className="flex items-center gap-1.5 text-red-500 text-xs font-semibold">
      <WifiOff size={13}/> Hors ligne
    </div>
  )
})

const QueueItem = memo(function QueueItem({
  item,
  onRetry,
  onRemove,
  isOnline,
  isRetrying,
  isSyncingThis,
}: {
  item:       ReturnType<typeof useAppStore.getState>['uploadQueue'][number]
  onRetry:    (contractId: string) => void
  onRemove:   (contractId: string) => void
  isOnline:   boolean
  isRetrying: boolean
  isSyncingThis: boolean
}) {
  const plate = item.inspection?.contract?.plate ?? item.contractId
  const type  = item.type ?? '–'
  const isFailed = item.status === 'failed'

  return (
    <div className={`flex items-center gap-3 bg-white border rounded-xl px-4 py-3 ${
      isSyncingThis ? 'border-blue-300 bg-blue-50/30' : isFailed ? 'border-red-200' : 'border-orange-200'
    }`}>
      <div className="flex-shrink-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
          isSyncingThis ? 'bg-blue-100' : isFailed ? 'bg-red-100' : 'bg-orange-100'
        }`}>
          {isSyncingThis ? (
            <RefreshCw size={16} className="text-blue-500 animate-spin"/>
          ) : isFailed ? (
            <AlertTriangle size={16} className="text-red-500"/>
          ) : (
            <Upload size={16} className="text-orange-500"/>
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 truncate">{plate}</p>
        <p className={`text-xs ${
          isSyncingThis ? 'text-blue-600 font-bold' : isFailed ? 'text-red-500 font-bold' : 'text-gray-500'
        }`}>
          {typeLabel(type)} · {isSyncingThis ? 'Synchronisation...' : isFailed ? 'Échec de synchronisation' : 'En attente sync'}
        </p>
        {item.inspection?.completedAt && (
          <p className="text-xs text-gray-400 mt-0.5">{fmtDate(item.inspection.completedAt)}</p>
        )}
        {(item.retries ?? 0) > 0 && (
          <p className={`text-xs mt-0.5 ${isFailed ? 'text-red-400 font-bold' : 'text-orange-400'}`}>
            {item.retries} tentative{item.retries > 1 ? 's' : ''} échouée{item.retries > 1 ? 's' : ''}
            {isFailed && ' — Action requise'}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {isOnline && !isSyncingThis && (
          <button
            onClick={() => onRetry(item.id)} // Utilise item.id pour retryQueueItem
            disabled={isRetrying}
            className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${
              isFailed ? 'bg-red-50 hover:bg-red-100 text-red-600' : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
            }`}
            title={isFailed ? 'Relancer manuellement' : 'Réessayer'}
          >
            <RefreshCw size={14} className={isRetrying ? 'animate-spin' : ''}/>
          </button>
        )}
        {!isSyncingThis && (
          <button
            onClick={() => onRemove(item.contractId)}
            className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
            title="Supprimer de la queue"
          >
            <Trash2 size={14}/>
          </button>
        )}
      </div>
    </div>
  )
})

// ── Sous-composant : carte brouillon CLIQUABLE ─────────────────────────────
const DraftItem = memo(function DraftItem({
  draft,
  onResume,
}: {
  draft:     DraftEntry
  onResume:  (draft: DraftEntry) => void   // ✅ NOUVEAU
}) {
  const displayName = draft.plate ?? draft.contractId

  return (
    <div
      onClick={() => onResume(draft)}
      className="flex items-center gap-3 bg-white border border-yellow-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-yellow-50 active:scale-[0.98] transition-all"
    >
      <div className="flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center">
          <Clock size={16} className="text-yellow-600"/>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 truncate">{displayName}</p>
        <p className="text-xs text-gray-500">{typeLabel(draft.type)} · Brouillon</p>
        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(draft.savedAt)}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs bg-yellow-100 text-yellow-700 font-semibold px-2 py-1 rounded-full">
          Brouillon
        </span>
        <ChevronRight size={16} className="text-gray-400"/>   {/* ✅ NOUVEAU */}
      </div>
    </div>
  )
})

// ── Sous-composant : carte photos offline ──────────────────────────────────
const PhotoItem = memo(function PhotoItem({ entry }: { entry: PhotoGroup }) {
  return (
    <div className="flex items-center gap-3 bg-white border border-purple-200 rounded-xl px-4 py-3">
      <div className="flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
          <Camera size={16} className="text-purple-500"/>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 truncate">{entry.contractId}</p>
        <p className="text-xs text-gray-500">{typeLabel(entry.type)} · Photos offline</p>
      </div>
      <div className="flex-shrink-0">
        <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-1 rounded-full">
          {entry.count} photo{entry.count > 1 ? 's' : ''}
        </span>
      </div>
    </div>
  )
})

// ── Composant principal ────────────────────────────────────────────────────
export default function PendingQueueScreen() {
  const uploadQueue      = useAppStore(s => s.uploadQueue)
  const removeFromQueue  = useAppStore(s => s.removeFromUploadQueue)
  const processQueue     = useAppStore(s => s.processUploadQueue)
  const setStep          = useAppStore(s => s.setStep)
  const restoreFromDraft = useAppStore(s => s.restoreFromDraft)
  const retryQueueItem   = useAppStore(s => s.retryQueueItem)

  const [isOnline,    setIsOnline]    = useState(navigator.onLine)
  const [drafts,      setDrafts]      = useState<DraftEntry[]>([])
  const [photoGroups, setPhotoGroups] = useState<PhotoGroup[]>([])
  const [isRetrying,  setIsRetrying]  = useState(false)
  const isSyncing   = useSyncStatus(s => s.isSyncing)
  const currentItem = useSyncStatus(s => s.currentItem)

  // ── Lecture IndexedDB ────────────────────────────────────────────────────
  const refreshIndexedDB = useCallback(async () => {
    try {
      const [allDrafts, allGroups] = await Promise.all([
        edlDraftStore.getAll(),
        offlinePhotoStore.getAllGroups(),
      ])
      setDrafts(
        allDrafts.map((d: any) => ({
          contractId: d.contractId ?? '–',
          type:       d.type       ?? 'retour',
          savedAt:    d.savedAt    ?? new Date().toISOString(),
          plate:      d.plate      ?? d.contract?.plate ?? undefined,
        }))
      )
      setPhotoGroups(allGroups)
    } catch (e) {
      console.warn('[PendingQueueScreen] IndexedDB read:', (e as Error).message)
    }
  }, [])

  // ── Événements réseau + focus + polling ──────────────────────────────────
  useEffect(() => {
    refreshIndexedDB()
    const onOnline  = () => { setIsOnline(true);  refreshIndexedDB() }
    const onOffline = () => setIsOnline(false)
    const onFocus   = () => refreshIndexedDB()
    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('focus',   onFocus)
    const timer = setInterval(refreshIndexedDB, POLL_MS)
    return () => {
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('focus',   onFocus)
      clearInterval(timer)
    }
  }, [refreshIndexedDB])

  // ── Reprise d'un brouillon ───────────────────────────────────────────────
  const handleResumeDraft = useCallback(async (draft: DraftEntry) => {
    try {
      const full = await edlDraftStore.get(draft.contractId, draft.type)
      if (!full) {
        toast.error('Brouillon introuvable')
        return
      }
      restoreFromDraft(full)                // ✅ restaure inspection + step dans le store
      toast.success(`Reprise de ${draft.plate ?? draft.contractId}`)
    } catch (e) {
      toast.error('Erreur lors de la reprise du brouillon')
      console.error('[handleResumeDraft]', e)
    }
  }, [restoreFromDraft])

  // ── Actions queue ────────────────────────────────────────────────────────
  const handleRetryAll = useCallback(async () => {
    // Si offline, on prévient mais on laisse tenter (le worker gérera l'échec fetch)
    if (!isOnline) {
      toast('Détection hors ligne — tentative de sync quand même...', { icon: '⚠️' })
    }
    
    // Remettre à 'pending' tous les items en 'failed'
    uploadQueue.forEach((item: any) => {
      if (item.status === 'failed') retryQueueItem(item.id)
    })

    setIsRetrying(true)
    try {
      await processQueue?.()
      await refreshIndexedDB()
      if (isOnline) toast.success('Synchronisation lancée ✓')
    } catch {
      toast.error('Erreur lors de la synchronisation')
    } finally {
      setIsRetrying(false)
    }
  }, [isOnline, uploadQueue, retryQueueItem, processQueue, refreshIndexedDB])

  const handleRetryOne = useCallback(async (id: string) => {
    if (!isOnline) {
      toast('Détection hors ligne — tentative...', { icon: '⚠️' })
    }

    // Remettre l'item spécifique à 'pending'
    retryQueueItem(id)

    setIsRetrying(true)
    try {
      // On récupère l'item pour avoir le contractId exigé par processQueue
      const item = uploadQueue.find((i: any) => i.id === id)
      await processQueue?.(item?.contractId)
      await refreshIndexedDB()
      if (isOnline) toast.success('Sync lancée ✓')
    } catch {
      toast.error('Erreur sync')
    } finally {
      setIsRetrying(false)
    }
  }, [isOnline, uploadQueue, retryQueueItem, processQueue, refreshIndexedDB])

  const handleRemove = useCallback((contractId: string) => {
    removeFromQueue?.(contractId)
    toast('EDL retiré de la queue', { icon: '🗑️' })
  }, [removeFromQueue])

  const handleBack = useCallback(() => {
    setStep('contracts')
  }, [setStep])

  // ── Calculs ──────────────────────────────────────────────────────────────
  const totalItems = uploadQueue.length + drafts.length + photoGroups.length
  const isEmpty    = totalItems === 0

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={handleBack}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
        >
          <ChevronLeft size={20}/>
        </button>
        <div className="flex-1">
          <h1 className="text-base font-black text-gray-800">File d'attente</h1>
          <p className="text-xs text-gray-400">
            {isEmpty
              ? 'Tout est synchronisé'
              : `${totalItems} élément${totalItems > 1 ? 's' : ''} en attente`}
          </p>
        </div>
        <NetworkBadge online={isOnline}/>
      </div>

      <div className="flex-1 p-4 flex flex-col gap-4 max-w-lg mx-auto w-full">

        {/* Bannière hors ligne */}
        {!isOnline && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <WifiOff size={15} className="text-orange-500 flex-shrink-0"/>
              <p className="text-orange-700 text-xs font-bold">
                Le navigateur se croit hors ligne
              </p>
            </div>
            <p className="text-[10px] text-orange-600 leading-tight">
              La détection réseau sur mobile peut être capricieuse. Si vous avez du réseau, vous pouvez tenter une 
              <button 
                onClick={handleRetryAll}
                className="ml-1 underline font-bold text-orange-800"
              >
                sync manuelle.
              </button>
            </p>
          </div>
        )}

        {/* État vide */}
        {isEmpty && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 size={32} className="text-green-500"/>
            </div>
            <div className="text-center">
              <p className="font-bold text-gray-700">Tout est synchronisé !</p>
              <p className="text-sm text-gray-400 mt-1">Aucun EDL en attente</p>
            </div>
            <Button onClick={handleBack} variant="secondary" size="lg">
              <ChevronLeft size={16}/> Retour
            </Button>
          </div>
        )}

        {/* Section : Upload queue */}
        {uploadQueue.length > 0 && (
          <Card className="p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <Upload size={14} className="text-orange-500"/>
                EDL en attente d'upload
                <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {uploadQueue.length}
                </span>
              </h2>
              <button
                onClick={handleRetryAll}
                disabled={isRetrying}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-40"
              >
                <RefreshCw size={12} className={isRetrying ? 'animate-spin' : ''}/>
                Tout sync
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {uploadQueue.map((item: any) => (
                <QueueItem
                  key={`${item.contractId}-${item.type ?? 'unknown'}`}
                  item={item}
                  onRetry={handleRetryOne}
                  onRemove={handleRemove}
                  isOnline={true}
                  isRetrying={isRetrying}
                  isSyncingThis={isSyncing && currentItem === item.contractId}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Section : Brouillons ✅ onResume branché */}
        {drafts.length > 0 && (
          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <Clock size={14} className="text-yellow-500"/>
              Brouillons EDL (En cours)
              <span className="bg-yellow-100 text-yellow-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {(() => {
                  const pendingIds = new Set(uploadQueue.map((i: any) => i.contractId))
                  return drafts.filter((d: any) => !pendingIds.has(d.contractId)).length
                })()}
              </span>
            </h2>
            <div className="flex flex-col gap-2">
              {(() => {
                const pendingIds = new Set(uploadQueue.map((i: any) => i.contractId))
                return drafts
                  .filter((d: any) => !pendingIds.has(d.contractId))
                  .map((d: any) => (
                    <DraftItem
                      key={`${d.contractId}-${d.type}`}
                      draft={d}
                      onResume={handleResumeDraft}
                    />
                  ))
              })()}
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <AlertTriangle size={11}/>
              Ces brouillons seront uploadés lors de la finalisation
            </p>
          </Card>
        )}

        {/* Section : Photos offline */}
        {photoGroups.length > 0 && (
          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <Camera size={14} className="text-purple-500"/>
              Photos en attente
              <span className="bg-purple-100 text-purple-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {photoGroups.reduce((acc, g) => acc + g.count, 0)}
              </span>
            </h2>
            <div className="flex flex-col gap-2">
              {photoGroups.map(g => (
                <PhotoItem key={`${g.contractId}-${g.type}`} entry={g}/>
              ))}
            </div>
          </Card>
        )}

        {/* CTA Retry global */}
        {!isEmpty && uploadQueue.length > 0 && (
          <Button
            onClick={handleRetryAll}
            disabled={isRetrying}
            size="lg"
            className="w-full"
          >
            <RefreshCw size={18} className={isRetrying ? 'animate-spin' : ''}/>
            {isRetrying ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </Button>
        )}

        {/* Info si brouillons/photos mais pas de queue active */}
        {!isEmpty && uploadQueue.length === 0 && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
            <Inbox size={15} className="text-blue-400 flex-shrink-0"/>
            <p className="text-xs text-blue-600 font-medium">
              Aucun EDL en attente d'upload – les brouillons et photos seront traités à la prochaine finalisation
            </p>
          </div>
        )}

      </div>
    </div>
  )
}